import React from 'react'
import { components, type ValueContainerProps } from 'react-select'

import { notEmpty } from '../../../../module'
import styles from '../_select.module.scss'

// Set custom `SingleValue` and `Placeholder` to keep them when searching
// See https://github.com/JedWatson/react-select/issues/3111#issuecomment-470911304

const ValueContainer = <OptionType,>({
  children,
  selectProps,
  ...props
}: ValueContainerProps<OptionType>) => {
  const commonProps = {
    cx: props.cx,
    clearValue: props.clearValue,
    getStyles: props.getStyles,
    getValue: props.getValue,
    hasValue: props.hasValue,
    isMulti: props.isMulti,
    isRtl: props.isRtl,
    options: props.options,
    selectOption: props.selectOption,
    setValue: props.setValue,
    selectProps,
    theme: props.theme,
    innerProps: props.innerProps ?? {},
    getClassNames: props.getClassNames,
    isDisabled: selectProps.isDisabled,
  }

  type SelectOption = OptionType & {
    display?: string
    image?: string
  }

  const selectedValue = selectProps.value as SelectOption,
    selectLabel = selectProps.labelKeyName as keyof OptionType

  const Placeholder = () => (
    <components.Placeholder
      {...commonProps}
      key='placeholder'
      isFocused={selectProps.isFocused || false}
    >
      {selectProps.placeholder}
    </components.Placeholder>
  )

  return (
    <components.ValueContainer {...props} selectProps={selectProps}>
      {React.Children.map(children, (child, index) => {
        const isFirstOption = index === 0,
          isEmptySelectedValue = !notEmpty(selectedValue?.[selectLabel]),
          selectedValueImage = selectedValue?.image

        const TextDisplay = () => {
          return (
            <components.SingleValue {...commonProps} data={props.getValue()[0]}>
              {selectProps.getOptionLabel(props.getValue()[0])}
            </components.SingleValue>
          )
        }

        const DisplayWithImage = () => (
          <components.SingleValue {...commonProps} data={props.getValue()[0]}>
            <div className={styles.imageContainer}>
              <img
                src={selectedValueImage}
                alt={selectedValue?.display}
                height={20}
              />
              {child ?? <TextDisplay />}
            </div>
          </components.SingleValue>
        )

        // Placeholder when no option is selected
        if (isEmptySelectedValue && isFirstOption) {
          return <Placeholder />
        }

        // This will display an image if the selected value has an image
        if (selectedValueImage && isFirstOption) {
          return <DisplayWithImage />
        }

        // This will display the selected / active option when the SearchBar is being used. When this is not present, then the display will be blank while searching.
        if (!child) {
          return <TextDisplay />
        }

        return child
      })}
    </components.ValueContainer>
  )
}

export default ValueContainer
